import { useTodayPacingMetrics } from "@expert/sales-tools";
import { Text } from "@mantine/core";

import sharedStyles from "../cards/cardContent.module.css";
import { MetricCard } from "../cards/MetricCard";
import { computeDirectProgress, computeMetricStatus, computeMetricTone } from "../cards/targetProgress";
import { PACE_TARGET_PERCENT } from "../cards/targets";

/**
 * Real-Time tab: share of calls kept on pace, from the gamification service — updates live.
 * Rendered for every partner with pacing (gated by {@link LiveMetrics}, which owns the column count).
 * Before any sessions land today it shows an empty state ("No calls today") rather than hiding,
 * so the card keeps its place in the grid from the start of shift.
 */
export function LivePaceCard() {
    const { data, isLoading, isError } = useTodayPacingMetrics();

    // "No calls today" is only honest when we actually have data showing zero sessions. A failed or
    // missing fetch falls back to "Unavailable" (matching LiveTotalCallsCard) so a service blip doesn't
    // read as an expert with no activity.
    const hasNoSessions = !!data && data.totalSessions === 0;
    const isEmpty = !isLoading && (isError || !data || hasNoSessions);
    const emptyValue = hasNoSessions ? "No calls today" : "Unavailable";

    const pct = data && data.totalSessions > 0 ? Math.round(data.onTargetRate * 100) : null;
    const progress = computeDirectProgress(pct, PACE_TARGET_PERCENT);
    const status = pct !== null ? computeMetricStatus(progress) : undefined;
    const tone = pct !== null ? computeMetricTone(progress) : undefined;

    return (
        <MetricCard
            title="On-target call pace"
            isLoading={isLoading}
            isEmpty={isEmpty}
            emptyValue={emptyValue}
            value={
                pct !== null ? (
                    <>
                        <Text component="span" className={sharedStyles.bigNumber}>
                            {pct}
                        </Text>
                        <Text component="span" className={sharedStyles.unit}>
                            %
                        </Text>
                    </>
                ) : null
            }
            footer={`${PACE_TARGET_PERCENT}% or higher`}
            status={status}
            tone={tone}
        />
    );
}
